import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import { type InputChangeEvent, InputText, Typography } from "@synapse-analytics/synapse-ui"

import { DataBlock } from "../../Ruleset/components/RuleCard"
import type { DefaultScoreProps } from "../Interfaces"

import styles from "./Scorecard.module.scss"

/**
 * @function DefaultScore
 * @description A React component that displays and allows editing of a default score.
 * This component is used within a scorecard and represents the score applied when no conditions match.
 * It supports read-only mode and input validation for numerical values.
 *
 * @param {string} value - The current value of the default score.
 * @param {(value: string) => void | undefined} [setValue] - Callback function to set the value of the default score.
 * @param {boolean} isReadMode - Flag to indicate if the component is in read-only mode.
 * @param {boolean} [isDisabled] - Flag to indicate if the input is disabled.
 * @returns {ReactElement} A React element representing the DefaultScore component.
 */
export function DefaultScore(props: Readonly<DefaultScoreProps>): ReactElement {
  const { value, setValue, isReadMode, isDisabled } = props

  const { t } = useTranslation("scorecards")

  /**
   * @function handleChange
   * @description Handles the change event of the InputText component.
   * Performs validation on the input value to ensure it's a valid number
   * without exponential notation.
   *
   * @param {InputChangeEvent} e - The input change event.
   */
  const handleChange = (e: InputChangeEvent): void => {
    const newValue = `${e.target.value}`

    if (newValue.includes("e")) return

    setValue && setValue(newValue)
  }

  return (
    <Grid container item xs={12} className={styles.defaultScoreRoot}>
      <Grid container item>
        <Typography variant="a" tooltip={t("defaultScoreToolTip")}>
          {t("defaultScore")}
        </Typography>
      </Grid>

      <Typography variant="h3-regular">=</Typography>

      <Grid item flex={"1 0 15%"} minWidth={110}>
        {isReadMode ? (
          <DataBlock value={value} />
        ) : (
          <InputText
            fullWidth
            hideDescription
            disabled={isDisabled}
            type="number"
            variant={"outlined"}
            value={value}
            handleChange={handleChange}
          />
        )}
      </Grid>
    </Grid>
  )
}
